'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';
import { useIsMounted, useMeasure } from '@react-hookz/web';
import clsx from 'clsx';
import {
  motion,
  useScroll,
  useTransform,
  type AnimationProps,
  type SVGMotionProps,
  type TapHandlers,
} from 'framer-motion';
import { Separator } from 'ui';

type ListItem = {
  key: string;
  item: JSX.Element;
};

export type HeaderProps = {
  logo: JSX.Element;
  mobileLogo?: JSX.Element;
  links: ListItem[];
  actions?: ListItem[];
  search?: JSX.Element;
  desktopClassName?: string;
  banner?: JSX.Element;
};

export const BLUR_STYLES = `bg-primary/80 backdrop-blur-lg`;

export const MOTION_PROPS: AnimationProps & TapHandlers = {
  variants: {
    open: {
      y: 0,
      opacity: 1,
      transition: {
        y: { stiffness: 1000, velocity: -100 },
      },
    },
    closed: {
      y: -50,
      opacity: 0,
      transition: {
        y: { stiffness: 1000 },
      },
    },
  },
  whileTap: { scale: 0.97 },
};

export function Header({
  links,
  logo,
  actions,
  search,
  desktopClassName,
  mobileLogo = logo,
  banner,
}: HeaderProps): JSX.Element {
  const [isOpen, setOpenMenu] = React.useState(false);
  const [measure, mobileNavRef] = useMeasure<HTMLDivElement>();
  const [bannerMeasure, bannerRef] = useMeasure<HTMLDivElement>();
  const { scrollY } = useScroll();
  const bannerHeight = bannerMeasure?.height ?? 0;
  const bannerOpacity = useTransform(
    scrollY,
    [0, 150], // scroll values
    [1, 0], // fade out while sliding up
  );
  const headerY = useTransform(
    scrollY,
    [0, 150], // scroll values
    banner ? [0, -bannerHeight] : [0, 0], // use measured height
  );

  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.removeProperty('overflow');
    }
  }, [isOpen]);
  const isMounted = useIsMounted();
  return (
    <div className="pb-22">
      <motion.header
        className={clsx(
          BLUR_STYLES,
          'fixed end-0 start-0 top-0',
          'z-10 border-b text-md font-semibold text-gray-600',
        )}
        style={{
          width: 'calc(100% - var(--removed-body-scroll-bar-size, 0px))',
          y: headerY,
        }}
      >
        {banner && (
          <motion.div
            ref={bannerRef}
            style={{
              opacity: bannerOpacity,
            }}
          >
            {banner}
          </motion.div>
        )}
        <div
          className={clsx(
            'mx-auto hidden max-w-7xl items-center justify-between gap-10 px-4 py-6 sm:flex',
            desktopClassName,
          )}
        >
          <div className="flex items-center gap-10">
            {logo}
            <nav className="flex gap-8">
              {links.map((link) => (
                <React.Fragment key={link.key}>{link.item}</React.Fragment>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-4">
            {search}
            <div className="flex items-center gap-3">
              {actions?.map((action) => (
                <React.Fragment key={action.key}>{action.item}</React.Fragment>
              ))}
            </div>
          </div>
        </div>
        {/* Mobile menu */}
        <motion.nav
          animate={isOpen ? 'open' : 'closed'}
          className="block sm:hidden"
          custom={measure?.height}
          initial={false}
        >
          <div
            className="flex items-center justify-between px-4 py-6"
            ref={mobileNavRef}
          >
            {mobileLogo}
            <MenuToggle
              toggle={() => {
                setOpenMenu((prev) => !prev);
              }}
            />
          </div>
          {isMounted() &&
            createPortal(
              <motion.ul
                className={clsx(
                  BLUR_STYLES,
                  'fixed start-0 flex h-screen w-full flex-col gap-2 py-6',
                )}
                style={{
                  top:
                    mobileNavRef.current?.getBoundingClientRect().bottom ?? 80,
                }}
                initial="closed"
                animate={isOpen ? 'open' : 'closed'}
                variants={{
                  open: {
                    clipPath: 'inset(0% 0% 0% 0%)',
                    transition: {
                      type: 'spring',
                      bounce: 0,
                      duration: 0.7,
                      delayChildren: 0.3,
                      staggerChildren: 0.05,
                    },
                  },
                  closed: {
                    clipPath: 'inset(0% 0% 100% 0%)',
                    transition: {
                      type: 'spring',
                      bounce: 0,
                      duration: 0.3,
                      delayChildren: 0,
                      staggerChildren: 0.05,
                      staggerDirection: -1,
                    },
                  },
                }}
              >
                {links.map((link) => (
                  <motion.li
                    onClick={() => {
                      setOpenMenu(false);
                    }}
                    {...MOTION_PROPS}
                    key={link.key}
                  >
                    {link.item}
                  </motion.li>
                ))}
                <motion.li {...MOTION_PROPS}>{search}</motion.li>
                {actions?.[0] && (
                  <>
                    <motion.div className="py-4" {...MOTION_PROPS}>
                      <Separator />
                    </motion.div>
                    {actions.map((action) => (
                      <motion.li
                        {...MOTION_PROPS}
                        className="flex flex-col gap-3"
                        key={action.key}
                      >
                        {action.item}
                      </motion.li>
                    ))}
                  </>
                )}
              </motion.ul>,
              document.body,
            )}
        </motion.nav>
      </motion.header>
    </div>
  );
}

export function Path(props: SVGMotionProps<SVGPathElement>) {
  return (
    <motion.path
      fill="transparent"
      stroke="rgb(var(--text-tertiary))"
      strokeLinecap="round"
      strokeWidth="2"
      {...props}
    />
  );
}

export function MenuToggle({ toggle }: { toggle: () => void }): JSX.Element {
  return (
    <button
      onClick={toggle}
      type="button"
      aria-label="Toggle Menu"
      className="align-middle"
    >
      {/* Translate y-0.5 to align center (the icon has a bottom padding) */}
      <svg viewBox="0 0 23 23" className="size-5.75 translate-y-0.5">
        <Path
          variants={{
            closed: { d: 'M 2 2.5 L 20 2.5' },
            open: { d: 'M 3 16.5 L 17 2.5' },
          }}
        />
        <Path
          d="M 2 9.423 L 20 9.423"
          transition={{ duration: 0.1 }}
          variants={{
            closed: { opacity: 1 },
            open: { opacity: 0 },
          }}
        />
        <Path
          variants={{
            closed: { d: 'M 2 16.346 L 20 16.346' },
            open: { d: 'M 3 2.5 L 17 16.346' },
          }}
        />
      </svg>
    </button>
  );
}
